import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
  getRedirectResult,
  getAdditionalUserInfo,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import axios from 'axios';
import { logger } from '../lib/logger';
import { BackendUser } from '../types/types';
import { UNVERIFIED_PIN_COUNT_KEY } from '../lib/appConstants';
import { randomAvatarFilename } from '../lib/avatarUtils';
import { updateUserProfile } from '../lib/userApi';

interface AuthContextType {
  currentUser: User | null;
  isLoading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  isAuthenticated: boolean;
  userType: string | null;
  checkUserInBackend: (uid: string, user?: User) => Promise<boolean>;
  backendUser: BackendUser | null;
  checkFeatureFlag: (flagName: string) => boolean;
  refreshUserData: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  reloadCurrentUser: () => Promise<void>;
}

// User type storage key
const USER_TYPE_STORAGE_KEY = 'user_type';

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userType, setUserType] = useState<string | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);

  // Helper to clear cached user-type data on sign-out
  const clearToken = useCallback(() => {
    try {
      localStorage.removeItem(USER_TYPE_STORAGE_KEY);
      localStorage.removeItem(UNVERIFIED_PIN_COUNT_KEY);
      setUserType(null);
    } catch (error) {
      logger.error('Error clearing auth token:', error);
    }
  }, []);

  // Function to get the current token (refreshed if possible)
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!currentUser) {
      return null;
    }

    try {
      // This will get a fresh token if the current one is expired
      const token = await getIdToken(currentUser, true);
      return token;
    } catch (error) {
      logger.error('Error getting auth token:', error);
      return null;
    }
  }, [currentUser]);

  // Check if user exists in backend
  const checkUserInBackend = useCallback(
    async (uid: string, user?: User): Promise<boolean> => {
      try {
        // Use the passed user or fall back to currentUser
        const userForToken = user || currentUser;
        if (!userForToken) {
          return false;
        }

        const token = await getIdToken(userForToken);

        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await axios.get<BackendUser>(`${apiBaseUrl}/users/firebase/${uid}/`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        // If we get a successful response, store user type
        if (response.data && response.data.user_type) {
          try {
            localStorage.setItem(USER_TYPE_STORAGE_KEY, response.data.user_type);
            setUserType(response.data.user_type);
            let userData = response.data;
            setBackendUser(userData);

            // Assign a default avatar for users who don't have one yet.
            // Google-authed users get their Google profile photo; everyone
            // else (and Google users without a photoURL) gets a random
            // bundled avatar.
            if (!userData.user_preferences?.avatar) {
              try {
                const googlePhotoUrl = userForToken.photoURL || undefined;
                const updated = await updateUserProfile(userData.id, {
                  user_preferences: {
                    ...userData.user_preferences,
                    avatar: googlePhotoUrl || randomAvatarFilename(),
                  },
                });
                setBackendUser(updated);
              } catch (avatarErr) {
                logger.error('Failed to assign default avatar:', avatarErr);
              }
            }
          } catch (error) {
            logger.error('Error storing user data:', error);
          }
        }

        return true;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return false; // User not found
        }
        logger.error('Error checking user in backend:', error);
        return false;
      }
    },
    [getToken]
  );

  const checkFeatureFlag = useCallback(
    (flagName: string): boolean => {
      if (!backendUser?.feature_flags) return false;
      const flag = backendUser.feature_flags.find(f => f.flag_name === flagName);
      return flag?.is_enabled || false;
    },
    [backendUser]
  );

  const refreshUserData = useCallback(async (): Promise<void> => {
    if (currentUser) {
      await checkUserInBackend(currentUser.uid, currentUser);
    }
  }, [currentUser, checkUserInBackend]);

  useEffect(() => {
    // Try to load user type from localStorage on startup
    try {
      const storedUserType = localStorage.getItem(USER_TYPE_STORAGE_KEY);

      if (storedUserType) {
        setUserType(storedUserType);
      }
    } catch (error) {
      logger.error('Error reading stored data:', error);
    }

    // Handle return from Google redirect sign-in (mobile)
    handleRedirectResult();

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);

      if (user) {
        // Check if user exists in backend and get their user type
        try {
          await checkUserInBackend(user.uid, user);
        } catch (error) {
          logger.error('Error on auth state change:', error);
        }
      } else {
        // User is signed out, clear their user type
        clearToken();
      }

      setIsLoading(false);
    });

    // Cleanup subscription
    return unsubscribe;
  }, [clearToken, checkUserInBackend]);

  // Internal helper — registers a new user with the Django backend
  const registerWithBackend = async (
    uid: string,
    email: string,
    displayName: string | null,
    token: string
  ): Promise<void> => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/register/`;
      const response = await axios.post<BackendUser>(
        apiUrl,
        {
          firebase_uid: uid,
          email,
          display_name: displayName,
          user_type: 'consumer',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setBackendUser(response.data);
      setUserType(response.data.user_type);
      localStorage.setItem(USER_TYPE_STORAGE_KEY, response.data.user_type);
    } catch (error) {
      logger.error('Backend registration failed:', error);
      // Don't throw — Firebase user exists, backend can be retried
    }
  };

  const sendVerificationEmail = async (): Promise<void> => {
    if (currentUser) {
      await sendEmailVerification(currentUser);
    }
  };

  const reloadCurrentUser = async (): Promise<void> => {
    if (currentUser) {
      await currentUser.reload();
      const refreshed = auth.currentUser;
      setCurrentUser(refreshed);
      // Clear the pin counter once the user has verified their email
      if (refreshed?.emailVerified) {
        localStorage.removeItem(UNVERIFIED_PIN_COUNT_KEY);
      }
    }
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const token = await getIdToken(result.user);
    await registerWithBackend(result.user.uid, email, displayName || null, token);
    // Fire verification email — don't await, don't block signup flow
    sendEmailVerification(result.user).catch(err =>
      logger.error('Failed to send verification email:', err)
    );
    return result.user;
  };

  const signInWithGoogle = async (): Promise<User> => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const additionalInfo = getAdditionalUserInfo(result);
    if (additionalInfo?.isNewUser) {
      const token = await getIdToken(result.user);
      await registerWithBackend(
        result.user.uid,
        result.user.email || '',
        result.user.displayName || null,
        token
      );
    }
    return result.user;
  };

  // Handles the return trip from signInWithRedirect — runs once on app load
  const handleRedirectResult = useCallback(async () => {
    try {
      const result = await getRedirectResult(auth);
      if (!result) return; // No redirect in progress
      const additionalInfo = getAdditionalUserInfo(result);
      if (additionalInfo?.isNewUser) {
        const token = await getIdToken(result.user);
        await registerWithBackend(
          result.user.uid,
          result.user.email || '',
          result.user.displayName || null,
          token
        );
      }
    } catch (error) {
      logger.error('Redirect sign-in error:', error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const logout = async () => {
    await signOut(auth);
    // Token will be cleared by the onAuthStateChanged listener
  };

  const value = {
    currentUser,
    isLoading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    getToken,
    isAuthenticated: !!currentUser,
    userType,
    backendUser,
    checkUserInBackend,
    checkFeatureFlag,
    refreshUserData,
    sendVerificationEmail,
    reloadCurrentUser,
  };

  return <AuthContext.Provider value={value}>{!isLoading && children}</AuthContext.Provider>;
};
